import React, { useState } from 'react';
import { Select, message, Tooltip, Modal } from 'antd';
import { SyncOutlined, CheckCircleFilled, ExclamationCircleFilled, WarningFilled, InfoCircleOutlined, DownOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { postAuthMethod } from '../../services/HttpServices';
import moment from 'moment';

export const WhatsAppQualityBadge = ({ rating = 'RED', compact = false }) => {
  const rawQuality = (rating || 'RED').toUpperCase();

  if (rawQuality.includes('GREEN') || rawQuality.includes('HIGH')) {
    return (
      <span className={`tw-inline-flex tw-items-center ${compact ? 'tw-gap-1 tw-px-1.5 tw-py-0.5 tw-text-[9.5px]' : 'tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px]'} tw-rounded-full tw-font-bold tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-200/80 tw-whitespace-nowrap tw-shadow-sm`}>
        <CheckCircleFilled className="tw-text-emerald-500 tw-text-[9px]" />
        <span>HIGH QUALITY</span>
      </span>
    );
  }
  if (rawQuality.includes('YELLOW') || rawQuality.includes('MEDIUM')) {
    return (
      <span className={`tw-inline-flex tw-items-center ${compact ? 'tw-gap-1 tw-px-1.5 tw-py-0.5 tw-text-[9.5px]' : 'tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px]'} tw-rounded-full tw-font-bold tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-solid tw-border-amber-200/80 tw-whitespace-nowrap tw-shadow-sm`}>
        <WarningFilled className="tw-text-amber-500 tw-text-[9px]" />
        <span>MEDIUM QUALITY</span>
      </span>
    );
  }
  return (
    <span className={`tw-inline-flex tw-items-center ${compact ? 'tw-gap-1 tw-px-1.5 tw-py-0.5 tw-text-[9.5px]' : 'tw-gap-1.5 tw-px-2.5 tw-py-1 tw-text-[11px]'} tw-rounded-full tw-font-bold tw-bg-rose-50 tw-text-rose-700 tw-border tw-border-solid tw-border-rose-200/80 tw-whitespace-nowrap tw-shadow-sm`}>
      <ExclamationCircleFilled className="tw-text-rose-500 tw-text-[9px]" />
      <span>POOR QUALITY</span>
    </span>
  );
};

export const WhatsAppLimitsModal = ({ open, onCancel }) => {
  return (
    <Modal
      title={null}
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      width={540}
      className="[&_.ant-modal-content]:!tw-rounded-2xl [&_.ant-modal-content]:!tw-p-0 [&_.ant-modal-content]:!tw-overflow-hidden"
    >
      {/* Modal Header Bar */}
      <div className="tw-bg-[#F8FAFC] tw-px-6 tw-py-4 tw-border-b tw-border-b-solid tw-border-slate-200/80 tw-flex tw-items-center tw-justify-between">
        <div className="tw-flex tw-items-center tw-gap-3">
          <div className="tw-size-9 tw-rounded-xl tw-bg-indigo-50 tw-text-[#5D44FF] tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
            <InfoCircleOutlined className="tw-text-lg" />
          </div>
          <div>
            <h3 className="tw-text-base tw-font-bold tw-text-slate-900 tw-m-0">Understanding WhatsApp Limits</h3>
            <p className="tw-text-[11px] tw-text-slate-500 tw-m-0 tw-font-normal">Quota rules, 24-hour service windows & quality ratings</p>
          </div>
        </div>
      </div>

      {/* Modal Body Content */}
      <div className="tw-p-6 tw-text-slate-600 tw-text-sm tw-space-y-4">
        {/* Intro Notice Banner */}
        <div className="tw-bg-indigo-50/60 tw-border tw-border-solid tw-border-indigo-100 tw-rounded-xl tw-p-3.5 tw-flex tw-items-start tw-gap-3">
          <SafetyCertificateOutlined className="tw-text-[#5D44FF] tw-text-base tw-mt-0.5 tw-flex-shrink-0" />
          <p className="tw-text-xs tw-text-slate-700 tw-m-0 tw-leading-relaxed">
            Meta enforces a <strong>24-hour messaging limit</strong> on the number of new unique business-initiated conversations you can open per day.
          </p>
        </div>

        {/* Section 1: What is a Conversation */}
        <div>
          <h4 className="tw-text-xs tw-font-bold tw-text-slate-800 tw-uppercase tw-tracking-wider tw-mb-2">
            What is a 24-Hour Conversation Window?
          </h4>
          <div className="tw-bg-slate-50 tw-rounded-xl tw-p-3 tw-border tw-border-solid tw-border-slate-200/80 tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-2">
            <div className="tw-bg-white tw-p-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-100 tw-text-center tw-shadow-sm">
              <div className="tw-text-[11px] tw-font-bold tw-text-indigo-600 tw-mb-1">1. Message Sent</div>
              <p className="tw-text-[11px] tw-text-slate-500 tw-m-0">Business initiates first message</p>
            </div>
            <div className="tw-bg-white tw-p-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-100 tw-text-center tw-shadow-sm">
              <div className="tw-text-[11px] tw-font-bold tw-text-emerald-600 tw-mb-1">2. Window Opens</div>
              <p className="tw-text-[11px] tw-text-slate-500 tw-m-0">Unlimited messages for 24h</p>
            </div>
            <div className="tw-bg-white tw-p-2.5 tw-rounded-lg tw-border tw-border-solid tw-border-slate-100 tw-text-center tw-shadow-sm">
              <div className="tw-text-[11px] tw-font-bold tw-text-slate-700 tw-mb-1">3. Window Expires</div>
              <p className="tw-text-[11px] tw-text-slate-500 tw-m-0">Quota slot frees up after 24h</p>
            </div>
          </div>
        </div>

        {/* Section 2: How usage is estimated */}
        <div>
          <h4 className="tw-text-xs tw-font-bold tw-text-slate-800 tw-uppercase tw-tracking-wider tw-mb-1.5">
            How Estimated Usage Works
          </h4>
          <p className="tw-text-xs tw-text-slate-600 tw-m-0 tw-leading-relaxed">
            Since Meta updates real-time quota data periodically, we calculate your active daily usage by counting the unique contacts messaged within the rolling 24-hour window.
          </p>
        </div>

        {/* Section 3: Quality Rating Levels */}
        <div>
          <h4 className="tw-text-xs tw-font-bold tw-text-slate-800 tw-uppercase tw-tracking-wider tw-mb-2">
            Quality Rating Levels
          </h4>
          <div className="tw-space-y-2">
            <div className="tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-bg-emerald-50/50 tw-border tw-border-solid tw-border-emerald-100">
              <div className="tw-flex tw-items-center tw-gap-2">
                <CheckCircleFilled className="tw-text-emerald-500 tw-text-sm" />
                <span className="tw-text-xs tw-font-bold tw-text-emerald-900">High Quality (Green)</span>
              </div>
              <span className="tw-text-[11px] tw-text-emerald-700 tw-font-medium">Meta automatically upgrades messaging tier</span>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-bg-amber-50/50 tw-border tw-border-solid tw-border-amber-100">
              <div className="tw-flex tw-items-center tw-gap-2">
                <WarningFilled className="tw-text-amber-500 tw-text-sm" />
                <span className="tw-text-xs tw-font-bold tw-text-amber-900">Medium Quality (Yellow)</span>
              </div>
              <span className="tw-text-[11px] tw-text-amber-700 tw-font-medium">Approaching warning threshold</span>
            </div>

            <div className="tw-flex tw-items-center tw-justify-between tw-p-2.5 tw-rounded-xl tw-bg-rose-50/50 tw-border tw-border-solid tw-border-rose-100">
              <div className="tw-flex tw-items-center tw-gap-2">
                <ExclamationCircleFilled className="tw-text-rose-500 tw-text-sm" />
                <span className="tw-text-xs tw-font-bold tw-text-rose-900">Poor Quality (Red)</span>
              </div>
              <span className="tw-text-[11px] tw-text-rose-700 tw-font-medium">Risk of tier downgrade or restrictions</span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

const WhatsAppAccountHealthBadge = ({ phoneNumbers = [], onSyncComplete, onSelectPhone, isSidebar = false, isEmbedded = false, activePhoneId = null }) => {
  const [loading, setLoading] = useState(false);
  const getInitialPhoneId = () => {
    if (activePhoneId) return activePhoneId;
    try {
      const saved = localStorage.getItem("whatsapp_selected_phone_id");
      if (saved && phoneNumbers.some((p) => String(p.id) === String(saved))) {
        const found = phoneNumbers.find((p) => String(p.id) === String(saved));
        return found ? found.id : saved;
      }
    } catch (e) {}
    return phoneNumbers.length > 0 ? phoneNumbers[0].id : null;
  };
  const [selectedPhoneId, setSelectedPhoneId] = useState(getInitialPhoneId);
  const [localPhones, setLocalPhones] = useState(phoneNumbers);
  const [cooldown, setCooldown] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);

  const handlePhoneChange = (val) => {
    const matched =
      phoneNumbers.find((p) => String(p.id) === String(val)) ||
      displayPhones.find((p) => String(p.id) === String(val));
    const finalId = matched ? matched.id : val;
    setSelectedPhoneId(finalId);
    const wabaId =
      matched?.wabaId ||
      matched?.whatsappBusinessAccountId ||
      1;
    try {
      localStorage.setItem("whatsapp_selected_phone_id", String(finalId));
      if (wabaId) {
        localStorage.setItem("whatsapp_selected_waba_id", String(wabaId));
      }
    } catch (e) {}
    if (onSelectPhone) {
      onSelectPhone(finalId, wabaId);
    }
  };

  React.useEffect(() => {
    if (activePhoneId) {
      setSelectedPhoneId(activePhoneId);
    }
  }, [activePhoneId]);

  React.useEffect(() => {
    setLocalPhones(phoneNumbers);
    if (phoneNumbers && phoneNumbers.length > 0) {
      setSelectedPhoneId((prev) => {
        if (prev && phoneNumbers.some((p) => String(p.id) === String(prev))) {
          const found = phoneNumbers.find((p) => String(p.id) === String(prev));
          return found ? found.id : prev;
        }
        try {
          const saved = localStorage.getItem("whatsapp_selected_phone_id");
          if (saved && phoneNumbers.some((p) => String(p.id) === String(saved))) {
            const found = phoneNumbers.find((p) => String(p.id) === String(saved));
            return found ? found.id : saved;
          }
        } catch (e) {}
        return phoneNumbers[0].id;
      });
    }
  }, [phoneNumbers]);

  const displayPhones = (localPhones && localPhones.length > 0)
    ? localPhones
    : [{ id: 'default', displayPhoneNumber: phoneNumbers?.[0]?.displayPhoneNumber || '+91 98343 75...', qualityRating: 'RED', quotaSnapshot: { estimatedOpened: 0, tierLimit: 2000 } }];

  const activePhone = displayPhones.find(p => String(p.id) === String(selectedPhoneId)) || displayPhones[0];
  const quota = activePhone.quotaSnapshot || {};

  const handleSync = async () => {
    if (!selectedPhoneId || selectedPhoneId === 'default' || cooldown > 0) return;
    
    setLoading(true);
    try {
      const response = await postAuthMethod(`/api/whatsapp/accounts/phone-numbers/${activePhone.phoneNumberId}/sync-status`);
      const updatedPhone = response.data;
      
      const newPhones = displayPhones.map(p => p.id === updatedPhone.id ? { ...p, ...updatedPhone } : p);
      setLocalPhones(newPhones);
      message.success('Account quality and limits synced from Meta');
      
      if (onSyncComplete) {
        onSyncComplete(newPhones);
      }
      
      setCooldown(15);
      const timer = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Failed to sync phone number status', error);
      message.error('Failed to sync data from Meta. Try again later.');
    } finally {
      setLoading(false);
    }
  };

  const formatLimit = (limit) => {
    if (limit === null || limit === undefined) return '2,000';
    if (limit > 1000000) return 'Unlimited';
    return limit.toLocaleString();
  };

  const estimatedOpened = quota.estimatedOpened || 0;
  const tierLimit = quota.tierLimit || 2000;
  const percentUsed = tierLimit > 0 ? Math.min(100, Math.round((estimatedOpened / tierLimit) * 100)) : 0;
  const isDanger = percentUsed >= 95;
  const isWarning = percentUsed >= 80 && percentUsed < 95;

  const rawQuality = (quota.quality || activePhone.qualityRating || 'RED').toUpperCase();

  const getQualityBadge = () => {
    if (rawQuality.includes('GREEN') || rawQuality.includes('HIGH')) {
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-bold tw-bg-emerald-50 tw-text-emerald-700 tw-border tw-border-solid tw-border-emerald-200/80 tw-whitespace-nowrap tw-shadow-sm">
          <CheckCircleFilled className="tw-text-emerald-500 tw-text-[11px]" />
          <span>HIGH QUALITY</span>
        </span>
      );
    }
    if (rawQuality.includes('YELLOW') || rawQuality.includes('MEDIUM')) {
      return (
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-bold tw-bg-amber-50 tw-text-amber-700 tw-border tw-border-solid tw-border-amber-200/80 tw-whitespace-nowrap tw-shadow-sm">
          <WarningFilled className="tw-text-amber-500 tw-text-[11px]" />
          <span>MEDIUM QUALITY</span>
        </span>
      );
    }
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-1 tw-rounded-full tw-text-[11px] tw-font-bold tw-bg-rose-50 tw-text-rose-700 tw-border tw-border-solid tw-border-rose-200/80 tw-whitespace-nowrap tw-shadow-sm">
        <ExclamationCircleFilled className="tw-text-rose-500 tw-text-[11px]" />
        <span>POOR QUALITY</span>
      </span>
    );
  };

  if (isSidebar) {
    if (isEmbedded) {
      return (
        <div className="tw-mt-2.5 tw-pt-2.5 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-space-y-2">
          {/* Estimated Usage & Progress */}
          <div className="tw-space-y-1">
            <div className="tw-flex tw-justify-between tw-items-center">
              <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">
                Estimated Usage
              </span>
              <span className={`tw-text-xs tw-font-bold ${isDanger ? 'tw-text-rose-600' : isWarning ? 'tw-text-amber-600' : 'tw-text-slate-900'}`}>
                {estimatedOpened.toLocaleString()} / {formatLimit(tierLimit)}
              </span>
            </div>
            <div className="tw-w-full tw-bg-slate-100 tw-rounded-full tw-h-1.5 tw-overflow-hidden">
              <div 
                className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${
                  isDanger ? 'tw-bg-rose-500' : isWarning ? 'tw-bg-amber-500' : 'tw-bg-[#5D44FF]'
                }`} 
                style={{ width: `${percentUsed}%` }}
              />
            </div>
          </div>

          {/* Updated Time & Actions Footer */}
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
            <span className="tw-text-[10px] tw-font-medium tw-text-slate-400 tw-whitespace-nowrap">
              Updated {quota.lastSynced ? moment(quota.lastSynced).fromNow() : 'an hour ago'}
            </span>

            <div className="tw-flex tw-items-center tw-gap-1.5">
              <Tooltip title={cooldown > 0 ? `Wait ${cooldown}s` : "Sync status with Meta"}>
                <button
                  type="button"
                  onClick={handleSync}
                  disabled={loading || cooldown > 0}
                  className="tw-w-6 tw-h-6 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-slate-100 hover:tw-bg-slate-200/80 tw-text-slate-600 hover:tw-text-[#5D44FF] tw-transition-colors tw-border-0 tw-cursor-pointer disabled:tw-opacity-50"
                >
                  <SyncOutlined spin={loading} className="tw-text-[10px]" />
                </button>
              </Tooltip>

              <Tooltip title="Understanding WhatsApp Limits">
                <button 
                  type="button" 
                  onClick={() => setIsModalVisible(true)} 
                  className="tw-w-6 tw-h-6 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-[#EFEAFF] hover:tw-bg-[#5D44FF] tw-text-[#5D44FF] hover:tw-text-white tw-transition-all tw-border-0 tw-cursor-pointer"
                >
                  <InfoCircleOutlined className="tw-text-[11px]" />
                </button>
              </Tooltip>
            </div>
          </div>

          <WhatsAppLimitsModal open={isModalVisible} onCancel={() => setIsModalVisible(false)} />
        </div>
      );
    }

    return (
      <div className="tw-bg-white tw-border tw-border-solid tw-border-slate-200/90 tw-rounded-2xl tw-p-3.5 tw-mb-3.5 tw-shadow-[0_4px_20px_rgba(0,0,0,0.03)] tw-space-y-3">
        {/* Phone Selector Header */}
        <div>
          {displayPhones.length > 1 ? (
            <Select
              value={activePhone?.id ?? (selectedPhoneId || displayPhones[0].id)}
              onChange={handlePhoneChange}
              style={{ width: "100%" }}
              size="small"
              bordered={false}
              className="!tw-bg-slate-50 !tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/80 tw-font-medium tw-text-xs [&_.ant-select-selector]:!tw-rounded-xl [&_.ant-select-selector]:!tw-px-3 [&_.ant-select-selector]:!tw-py-1 [&_.ant-select-selection-item]:!tw-font-semibold [&_.ant-select-selection-item]:!tw-text-slate-800"
              options={displayPhones.map((phone) => ({
                value: phone.id,
                label: phone.displayPhoneNumber || phone.phoneNumber || String(phone.id),
              }))}
            />
          ) : (
            <div className="tw-px-3 tw-py-1.5 tw-rounded-xl tw-bg-slate-50 tw-border tw-border-solid tw-border-slate-200/80 tw-flex tw-items-center tw-justify-between">
              <div className="tw-flex tw-items-center tw-gap-2">
                <span className="tw-size-2 tw-rounded-full tw-bg-emerald-500 tw-animate-pulse" />
                <span className="tw-text-xs tw-font-semibold tw-text-slate-800">
                  {activePhone?.displayPhoneNumber || activePhone?.phoneNumber || '+91 98343 75819'}
                </span>
              </div>
              <DownOutlined className="tw-text-[10px] tw-text-slate-400" />
            </div>
          )}
        </div>

        {/* Quality Rating & Action Bar */}
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
          <div className="tw-flex tw-items-center tw-gap-1.5 tw-min-w-0">
            <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">Quality:</span>
            {getQualityBadge()}
          </div>

          <div className="tw-flex tw-items-center tw-gap-1 tw-flex-shrink-0">
            <Tooltip title={cooldown > 0 ? `Wait ${cooldown}s` : "Sync status with Meta"}>
              <button
                type="button"
                onClick={handleSync}
                disabled={loading || cooldown > 0}
                className="tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-slate-100 hover:tw-bg-slate-200/80 tw-text-slate-600 hover:tw-text-[#5D44FF] tw-transition-colors tw-border-0 tw-cursor-pointer disabled:tw-opacity-50"
              >
                <SyncOutlined spin={loading} className="tw-text-[11px]" />
              </button>
            </Tooltip>

            <Tooltip title="Understanding WhatsApp Limits">
              <button 
                type="button" 
                onClick={() => setIsModalVisible(true)} 
                className="tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-[#EFEAFF] hover:tw-bg-[#5D44FF] tw-text-[#5D44FF] hover:tw-text-white tw-transition-all tw-border-0 tw-cursor-pointer"
              >
                <InfoCircleOutlined className="tw-text-xs" />
              </button>
            </Tooltip>
          </div>
        </div>

        {/* Estimated Usage & Progress */}
        <div className="tw-pt-2 tw-border-t tw-border-b-0 tw-border-l-0 tw-border-r-0 tw-border-solid tw-border-slate-100 tw-space-y-1.5">
          <div className="tw-flex tw-justify-between tw-items-center">
            <span className="tw-text-[10px] tw-font-bold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">
              Usage ({quota.lastSynced ? moment(quota.lastSynced).fromNow() : 'an hour ago'})
            </span>
            <span className={`tw-text-xs tw-font-bold ${isDanger ? 'tw-text-rose-600' : isWarning ? 'tw-text-amber-600' : 'tw-text-slate-900'}`}>
              {estimatedOpened.toLocaleString()} / {formatLimit(tierLimit)}
            </span>
          </div>
          <div className="tw-w-full tw-bg-slate-100 tw-rounded-full tw-h-2 tw-overflow-hidden">
            <div 
              className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${
                isDanger ? 'tw-bg-rose-500' : isWarning ? 'tw-bg-amber-500' : 'tw-bg-[#5D44FF]'
              }`} 
              style={{ width: `${percentUsed}%` }}
            />
          </div>
        </div>

        <WhatsAppLimitsModal open={isModalVisible} onCancel={() => setIsModalVisible(false)} />
      </div>
    );
  }

  return (
    <div className="tw-inline-flex tw-items-center tw-gap-3.5 sm:tw-gap-4 tw-bg-white tw-px-3.5 sm:tw-px-4.5 tw-py-2.5 tw-rounded-2xl tw-border tw-border-solid tw-border-slate-200/90 tw-shadow-sm tw-transition-all tw-flex-wrap lg:tw-flex-nowrap">
      
      {/* Phone selector or number pill */}
      {displayPhones.length > 1 ? (
        <Select
          value={activePhone?.id ?? (selectedPhoneId || displayPhones[0].id)}
          onChange={handlePhoneChange}
          style={{ width: 180 }}
          size="small"
          bordered={false}
          className="!tw-bg-slate-50 !tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/80 tw-font-medium tw-text-xs [&_.ant-select-selector]:!tw-rounded-xl [&_.ant-select-selector]:!tw-px-3 [&_.ant-select-selector]:!tw-py-1 [&_.ant-select-selection-item]:!tw-font-semibold [&_.ant-select-selection-item]:!tw-text-slate-800"
          options={displayPhones.map((phone) => ({
            value: phone.id,
            label: phone.displayPhoneNumber || phone.phoneNumber || String(phone.id),
          }))}
        />
      ) : (
        <div className="tw-px-3.5 tw-py-1.5 tw-rounded-xl tw-border tw-border-solid tw-border-slate-200/90 tw-bg-slate-50 tw-text-xs tw-font-semibold tw-text-slate-800 tw-flex tw-items-center tw-gap-2 tw-whitespace-nowrap tw-flex-shrink-0">
          <span className="tw-size-2 tw-rounded-full tw-bg-emerald-500 tw-animate-pulse tw-flex-shrink-0" />
          <span>{activePhone?.displayPhoneNumber || activePhone?.phoneNumber || '+91 98343 75819'}</span>
          <DownOutlined className="tw-text-[10px] tw-text-slate-400" />
        </div>
      )}

      {/* Divider */}
      <div className="tw-hidden sm:tw-block tw-w-px tw-h-7 tw-bg-slate-200/80 tw-flex-shrink-0"></div>

      {/* Quality Rating */}
      <div className="tw-flex tw-flex-col tw-justify-center">
        <span className="tw-text-[10px] tw-text-[#8C8C8C] tw-font-bold tw-uppercase tw-tracking-wider tw-mb-0.5">
          QUALITY RATING
        </span>
        {getQualityBadge()}
      </div>

      {/* Divider */}
      <div className="tw-hidden sm:tw-block tw-w-px tw-h-7 tw-bg-slate-200/80 tw-flex-shrink-0"></div>

      {/* Progress Bar & Usage */}
      <div className="tw-flex tw-flex-col tw-justify-center tw-min-w-[150px] sm:tw-min-w-[180px]">
        <div className="tw-flex tw-justify-between tw-items-center tw-mb-1">
          <span className="tw-text-[10px] tw-text-[#8C8C8C] tw-font-bold tw-uppercase tw-tracking-wider">
            ESTIMATED USAGE
          </span>
          <span className={`tw-text-xs tw-font-bold ${isDanger ? 'tw-text-rose-600' : isWarning ? 'tw-text-amber-600' : 'tw-text-slate-900'}`}>
            {estimatedOpened.toLocaleString()} / {formatLimit(tierLimit)}
          </span>
        </div>
        <div className="tw-w-full tw-bg-slate-100 tw-rounded-full tw-h-2 tw-overflow-hidden">
          <div 
            className={`tw-h-full tw-rounded-full tw-transition-all tw-duration-500 ${
              isDanger ? 'tw-bg-rose-500' : isWarning ? 'tw-bg-amber-500' : 'tw-bg-[#5D44FF]'
            }`} 
            style={{ width: `${percentUsed}%` }}
          />
        </div>
      </div>

      {/* Divider */}
      <div className="tw-hidden sm:tw-block tw-w-px tw-h-7 tw-bg-slate-200/80 tw-flex-shrink-0"></div>

      {/* Last Synced & Actions */}
      <div className="tw-flex tw-items-center tw-gap-2.5">
        <div className="tw-flex tw-flex-col tw-justify-center">
          <span className="tw-text-[10px] tw-[#8C8C8C] tw-text-[#8C8C8C] tw-font-bold tw-uppercase tw-tracking-wider tw-mb-0.5">
            UPDATED
          </span>
          <span className="tw-text-xs tw-font-medium tw-text-slate-700 tw-whitespace-nowrap">
            {quota.lastSynced ? moment(quota.lastSynced).fromNow() : 'an hour ago'}
          </span>
        </div>

        <Tooltip title={cooldown > 0 ? `Wait ${cooldown}s` : "Sync status with Meta"}>
          <button
            type="button"
            onClick={handleSync}
            disabled={loading || cooldown > 0}
            className="tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-slate-100 hover:tw-bg-slate-200/80 tw-text-slate-600 hover:tw-text-[#5D44FF] tw-transition-colors tw-border-0 tw-cursor-pointer disabled:tw-opacity-50 tw-flex-shrink-0"
          >
            <SyncOutlined spin={loading} className="tw-text-xs" />
          </button>
        </Tooltip>

        <Tooltip title="Understanding WhatsApp Limits">
          <button 
            type="button" 
            onClick={() => setIsModalVisible(true)} 
            className="tw-w-7 tw-h-7 tw-rounded-lg tw-flex tw-items-center tw-justify-center tw-bg-[#EFEAFF] hover:tw-bg-[#5D44FF] tw-text-[#5D44FF] hover:tw-text-white tw-transition-all tw-border-0 tw-cursor-pointer tw-flex-shrink-0"
          >
            <InfoCircleOutlined className="tw-text-xs" />
          </button>
        </Tooltip>
      </div>

      <WhatsAppLimitsModal open={isModalVisible} onCancel={() => setIsModalVisible(false)} />

    </div>
  );
};

export default WhatsAppAccountHealthBadge;
